import React, { Suspense, useState } from 'react';
import { MemoryRouter } from 'react-router';
import { ThemeProvider } from '@emotion/react';
import { useMount } from 'react-use';
import { observer } from 'mobx-react';
import 'dayjs/locale/de';
import {
  ExternalFormatPrice,
  ExternalSearchParams,
  ExternalSearchParamsContextProps,
  LoadingOverlay,
  Overlay
} from '@ibe/components';
import theme from '../Theme/theme';
import { boot } from '../Config/config';
import initI18next from '../Translations/setup';
import ProductContainer from '../Components/ProductContainer';
import { ConfigModel, LoggerFactory, SessionStorage, formatPrice } from '@ibe/services';
import { ThgConfigModel } from '../Config/ThgConfigModel';
import { SessionStorageType } from '../Components/Search';
import '../Theme/Styles/widgets.scss';
import { productSearchSessionKey } from '../Config/sessionkeys';
import { extendedConfigContext } from '../Config/extendedConfigContext';

const logger = LoggerFactory.get('ProductWidget');

const sessionStorage = new SessionStorage<SessionStorageType>(productSearchSessionKey);

const ProductWidget = observer(
  (
    props: ExternalSearchParamsContextProps & {
      thgConfig: ThgConfigModel;
      configOverrides?: ConfigModel;
    }
  ) => {
    const { widgetSessionKeyPrefix: keyPrefix, thgConfig, configOverrides, mode } = props;
    const [bootstrappingFinished, setBootstrappingFinished] = useState<boolean>(false);
    const [sessionProp, setSessionProp] = useState<SessionStorageType | undefined>(undefined);

    const widgetSessionKeyPrefix = keyPrefix || document.body.id || '';

    useMount(async () => {
      try {
        await initI18next(
          thgConfig.baseUrl,
          thgConfig.version,
          configOverrides?.tenant ?? undefined,
          mode
        );
        await boot(false, widgetSessionKeyPrefix, configOverrides, true, thgConfig);
        const session = sessionStorage.get();
        if (!!session) {
          setSessionProp(session);
        }
      } catch (err) {
        logger.error(err);
      } finally {
        setBootstrappingFinished(true);
      }
    });

    return bootstrappingFinished ? (
      <ThemeProvider theme={theme}>
        <div style={{ backgroundColor: 'transparent' }} id="iso">
          <Suspense fallback={<Overlay fullOpacity />}>
            <extendedConfigContext.Provider value={thgConfig}>
              <MemoryRouter>
                <ExternalSearchParams {...props} widgetSessionKeyPrefix={widgetSessionKeyPrefix}>
                  <LoadingOverlay isLoading={!bootstrappingFinished}>
                    <ExternalFormatPrice externalFormatPrice={formatPrice}>
                      <ProductContainer sessionProp={sessionProp} setSessionProp={setSessionProp} />
                    </ExternalFormatPrice>
                  </LoadingOverlay>
                </ExternalSearchParams>
              </MemoryRouter>
            </extendedConfigContext.Provider>
          </Suspense>
        </div>
      </ThemeProvider>
    ) : (
      <div id="iso" style={{ height: '300px' }}>
        <Overlay />
      </div>
    );
  }
);

export default ProductWidget;
